import styled from 'styled-components';
import Matter, { Bodies, Body, Composite, Engine, Render, Runner } from 'matter-js';
import { useEffect, useRef, useState } from 'react';
import { PROJECT_THEME } from '../../theme';

/* Containers opt into the simulation with a data-physics attribute. */
const PHYSICS_TARGET = '[data-physics]';
/* The grid the cubes are dropped onto; they land on its top-right cell. */
const SPAWN_TARGET = '[data-physics-spawn]';
/* Things beside the landing spot that the pile must leave uncovered, such as a heading. */
const CLEAR_TARGET = '[data-physics-clear]';

const topRightCell = (): DOMRect | null => {
    const container = document.querySelector<HTMLElement>(SPAWN_TARGET);
    if (!container) return null;

    const cells = Array.from(container.querySelectorAll<HTMLElement>(PHYSICS_TARGET))
        .map((child) => child.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0);

    if (!cells.length) return null;

    const firstRowTop = Math.min(...cells.map((rect) => rect.top));
    return cells
        .filter((rect) => rect.top < firstRowTop + rect.height / 2)
        .reduce((widest, rect) => (rect.right > widest.right ? rect : widest));
};
const WALL_THICKNESS = 200;
const CUBE_COLUMNS = 6;
/* Cubes scale with the viewport; below this they read as confetti, so a narrow screen keeps
   this size and loses columns instead. */
const MIN_CUBE_SIZE = 36;
/* A phone gets a shorter, chunkier pile: three columns, grown to fill the room beside the heading. */
const PHONE_COLUMNS = 3;
const PHONE_MAX_CUBE_SIZE = 48;
const PHONE_QUERY = '(max-width: 40rem)';
/* The pile settles before the hint appears, and the hint goes once the visitor has played. */
const HINT_DELAY_MS = 1200;
const FADE_OUT_MS = 400;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

export type Hint = { mode: 'tilt' | 'tap' | 'mouse'; left: number; top: number; width: number; height: number };

/* Width available for the pile at the cell's right edge once anything it must not cover is excluded. */
const roomForPile = (cell: DOMRect, size: number): number => {
    const pileTop = cell.top - size * CUBE_COLUMNS;
    return Array.from(document.querySelectorAll<HTMLElement>(CLEAR_TARGET))
        .map((element) => element.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.bottom > pileTop && rect.top < cell.top)
        .reduce((room, rect) => Math.min(room, cell.right - rect.right - size / 4), cell.width);
};
/* Cube edge as a fraction of its grid cell, leaving a gap between neighbours. */
const CUBE_SIZE_RATIO = 0.92;

const documentSize = () => ({
    width: document.documentElement.scrollWidth,
    height: document.documentElement.scrollHeight,
});

const immovable = (x: number, y: number, width: number, height: number) =>
    Bodies.rectangle(x, y, width, height, {
        isStatic: true,
        friction: 0.8,
        frictionStatic: 1,
        restitution: 0,
        render: { visible: false },
    });

const collectObstacleRects = () => {
    const { width, height } = documentSize();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const containers = Array.from(document.querySelectorAll<HTMLElement>(PHYSICS_TARGET))
        .map((element) => element.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0)
        .map((rect) => ({
            x: Math.round(rect.left + scrollX + rect.width / 2),
            y: Math.round(rect.top + scrollY + rect.height / 2),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        }));

    return [
        ...containers,
        { x: -WALL_THICKNESS / 2, y: height / 2, width: WALL_THICKNESS, height: height * 2 },
        {
            x: width + WALL_THICKNESS / 2,
            y: height / 2,
            width: WALL_THICKNESS,
            height: height * 2,
        },
        { x: width / 2, y: height + WALL_THICKNESS / 2, width: width * 2, height: WALL_THICKNESS },
    ];
};

const pyramid = (x: number, y: number, size: number, columns: number, colours: string[]) => {
    const composite = Composite.create();

    for (let column = 0; column < columns; column++) {
        for (let row = 0; row <= column; row++) {
            Composite.add(
                composite,
                Bodies.rectangle(
                    x + column * size,
                    y - row * size,
                    size * CUBE_SIZE_RATIO,
                    size * CUBE_SIZE_RATIO,
                    {
                        density: 0.005,
                        friction: 0.4,
                        restitution: 0.05,
                        render: {
                            fillStyle: colours[column % colours.length],
                            strokeStyle: PROJECT_THEME.colour.DARK_GREY,
                            lineWidth: 1,
                        },
                    }
                )
            );
        }
    }

    return composite;
};

const homeAnimation = (element: HTMLElement, onHint: (hint: Hint | null) => void) => {
    const engine = Engine.create();
    const { world } = engine;

    const render = Render.create({
        element,
        engine,
        options: {
            width: window.innerWidth,
            height: window.innerHeight,
            background: 'transparent',
            wireframeBackground: 'transparent',
            wireframes: false,
            hasBounds: true,
        },
    });

    let immovableBodies: Matter.Body[] = [];
    let obstacleSignature = '';
    let viewportSize = { width: 0, height: 0 };
    /* Falling into a tight gap between static obstacles can leave a body with a
       degenerate contact that blows its position up to NaN, which then crashes
       every future collision pass. Teleporting it back to a clean drop point is
       cheaper and more robust than trying to prevent every such gap. */
    let respawnPoint = { x: 0, y: 0 };

    /* Bodies live in document space, so the view follows the scroll offset. */
    const syncBounds = () => {
        render.bounds.min.x = window.scrollX;
        render.bounds.min.y = window.scrollY;
        render.bounds.max.x = window.scrollX + viewportSize.width;
        render.bounds.max.y = window.scrollY + viewportSize.height;
    };

    const refresh = () => {
        if (viewportSize.width !== window.innerWidth || viewportSize.height !== window.innerHeight) {
            viewportSize = { width: window.innerWidth, height: window.innerHeight };
            render.options.width = viewportSize.width;
            render.options.height = viewportSize.height;
            render.canvas.width = viewportSize.width;
            render.canvas.height = viewportSize.height;
            Render.setPixelRatio(render, window.devicePixelRatio);
            syncBounds();
        }

        const rects = collectObstacleRects();
        const signature = rects.map((r) => `${r.x},${r.y},${r.width},${r.height}`).join('|');

        /* Rebuilding unchanged bodies would jostle anything resting on them. */
        if (signature === obstacleSignature) return;
        obstacleSignature = signature;

        Composite.remove(world, immovableBodies);
        immovableBodies = rects.map((r) => immovable(r.x, r.y, r.width, r.height));
        Composite.add(world, immovableBodies);
    };

    refresh();
    syncBounds();
    window.addEventListener('scroll', syncBounds, { passive: true });

    /* The only non-static bodies ever added to the world, so this list never changes
       after spawn; caching it avoids re-walking the whole composite on every mousemove. */
    let dynamicBodies: Matter.Body[] = [];

    let pile: Omit<Hint, 'mode'> | null = null;

    const spawnRect = topRightCell();
    if (spawnRect) {
        let size = Math.max(window.innerWidth / 27.7, MIN_CUBE_SIZE);
        let columns = clamp(Math.floor(roomForPile(spawnRect, size) / size), 2, CUBE_COLUMNS);
        if (window.matchMedia(PHONE_QUERY).matches) {
            columns = Math.min(columns, PHONE_COLUMNS);
            size = clamp(roomForPile(spawnRect, size) / columns, MIN_CUBE_SIZE, PHONE_MAX_CUBE_SIZE);
        }
        /* Right edge of the tallest column sits flush with the grid's right boundary. */
        const lastColumnCentre =
            spawnRect.right + window.scrollX - (size * CUBE_SIZE_RATIO) / 2;
        const originX = lastColumnCentre - (columns - 1) * size;
        const originY = spawnRect.top + window.scrollY - (size * CUBE_SIZE_RATIO) / 2;
        respawnPoint = { x: originX, y: originY - size * columns };
        const cubes = pyramid(originX, originY, size, columns, PROJECT_THEME.colour.PALETTE);
        Composite.add(world, cubes);
        dynamicBodies = Composite.allBodies(cubes);
        pile = {
            left: originX - (size * CUBE_SIZE_RATIO) / 2,
            top: originY - (columns - 1) * size - (size * CUBE_SIZE_RATIO) / 2,
            width: (columns - 1) * size + size * CUBE_SIZE_RATIO,
            height: (columns - 1) * size + size * CUBE_SIZE_RATIO,
        };
    }

    Render.run(render);
    const runner = Runner.create();

    /* A resting cube squeezed into an obstacle re-created (by refresh()) around its
       exact position can produce a degenerate contact that drives its position to
       NaN mid-tick. That NaN body then throws from inside every future Engine.update,
       before afterUpdate ever fires, so the only way to recover is to catch the tick
       itself and reset state directly, rather than relying on Matter's own events. */
    const resetFallingBodies = () => {
        for (const body of Composite.allBodies(world)) {
            if (body.isStatic) continue;
            Body.setPosition(body, respawnPoint);
            Body.setVelocity(body, { x: 0, y: 0 });
            Body.setAngularVelocity(body, 0);
        }
    };

    let frameId = 0;
    const tick = (time: number) => {
        frameId = requestAnimationFrame(tick);
        try {
            Runner.tick(runner, engine, time);
        } catch {
            resetFallingBodies();
        }
    };
    frameId = requestAnimationFrame(tick);

    let lastPointer: Matter.Vector | null = null;

    const onPointerMove = (event: MouseEvent) => {
        const pointer = { x: event.pageX, y: event.pageY };
        const previous = lastPointer ?? pointer;
        lastPointer = pointer;

        const hit = Matter.Query.point(dynamicBodies, pointer);
        if (hit.length) dismissHint();
        hit.forEach((body) =>
            Body.applyForce(body, pointer, {
                x: (pointer.x - previous.x) * 0.02,
                y: (pointer.y - previous.y) * 0.02,
            })
        );
    };

    let hintTimer = 0;
    let hintDismissed = false;
    let hintVisible = false;
    let hintMode: Hint['mode'] | null = null;

    /* The first call starts the delay; later calls change what the caption says. */
    const showHint = (mode: Hint['mode']) => {
        if (!pile || hintDismissed) return;
        hintMode = mode;
        if (hintVisible) onHint({ mode, ...pile });
        else if (!hintTimer) {
            hintTimer = window.setTimeout(() => {
                hintVisible = true;
                if (!hintDismissed && pile && hintMode) onHint({ mode: hintMode, ...pile });
            }, HINT_DELAY_MS);
        }
    };

    const dismissHint = () => {
        if (hintDismissed) return;
        hintDismissed = true;
        window.clearTimeout(hintTimer);
        onHint(null);
    };

    /* On a phone the pile answers to the device's tilt instead of a mouse. The screen's rotation
       decides which sensor axis is which, as in Matter's own gyro example. */
    const onTilt = (event: DeviceOrientationEvent) => {
        const beta = clamp(event.beta ?? 90, -90, 90) / 90;
        const gamma = clamp(event.gamma ?? 0, -90, 90) / 90;
        const angle = window.screen.orientation?.angle ?? 0;
        let x = gamma;
        let y = beta;
        if (angle === 90) {
            x = beta;
            y = -gamma;
        } else if (angle === 180) {
            y = -beta;
        } else if (angle === 270) {
            x = -beta;
            y = gamma;
        }
        /* Lying flat, the screen has no gravity across it; keep a little so the pile settles. */
        if (Math.hypot(x, y) < 0.2) y = 0.2;
        engine.gravity.x = x;
        engine.gravity.y = y;
        if (Math.abs(x) > 0.25) dismissHint();
    };

    const listenToTilt = () => window.addEventListener('deviceorientation', onTilt);

    /* Without tilt a pile on a phone is clutter, so it fades out and leaves the world. */
    const removeCubes = () => {
        dismissHint();
        const doomed = dynamicBodies;
        dynamicBodies = [];
        const started = performance.now();
        const fade = (now: number) => {
            const progress = Math.min(1, (now - started) / FADE_OUT_MS);
            doomed.forEach((body) => {
                body.render.opacity = 1 - progress;
            });
            if (progress < 1) requestAnimationFrame(fade);
            else Composite.remove(world, doomed);
        };
        requestAnimationFrame(fade);
    };

    const Orientation = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> };
    const tiltable = window.matchMedia('(pointer: coarse)').matches && 'DeviceOrientationEvent' in window;

    let asking = false;
    let granted = false;

    const settle = (state: string) => {
        asking = false;
        if (state === 'granted') {
            granted = true;
            window.removeEventListener('click', askOnPileTap);
            listenToTilt();
            showHint('tilt');
        } else {
            removeCubes();
        }
    };

    /* iOS shows its motion prompt only from inside a tap, so this runs from the pile's or the
       caption's click handler. */
    const requestTilt = () => {
        if (!Orientation.requestPermission || granted || asking) return;
        asking = true;
        Orientation.requestPermission()
            .then(settle)
            .catch(() => {
                asking = false;
            });
    };

    const askOnPileTap = (event: MouseEvent) => {
        if (Matter.Query.point(dynamicBodies, { x: event.pageX, y: event.pageY }).length) requestTilt();
    };

    let pending = 0;
    let lastMeasured = 0;

    /* Measuring forces layout, so cap it well below the frame rate; the card animations mutate constantly. */
    const MEASURE_INTERVAL_MS = 250;

    const scheduleRefresh = () => {
        if (pending) return;
        const wait = Math.max(0, MEASURE_INTERVAL_MS - (performance.now() - lastMeasured));
        pending = window.setTimeout(() => {
            pending = 0;
            lastMeasured = performance.now();
            refresh();
        }, wait);
    };

    const resizeObserver = new ResizeObserver(scheduleRefresh);
    resizeObserver.observe(document.body);

    /* Keeps obstacles in step with layout shifts such as the flow chart's fit-to-view zoom.
       childList is deliberately excluded: obstacles only ever move via a style/class change
       on some element, and the card animations add/remove DOM nodes constantly, which would
       otherwise force this to re-measure (and lay out) far more often than anything moves. */
    const mutationObserver = new MutationObserver(scheduleRefresh);
    mutationObserver.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
    });

    window.addEventListener('resize', scheduleRefresh);
    if (!tiltable) {
        window.addEventListener('mousemove', onPointerMove);
        showHint('mouse');
    } else if (!Orientation.requestPermission) {
        listenToTilt();
        showHint('tilt');
    } else {
        /* An answer the browser remembers comes back without a tap; otherwise the pile waits for one. */
        Orientation.requestPermission()
            .then(settle)
            .catch(() => {
                showHint('tap');
                window.addEventListener('click', askOnPileTap);
            });
    }

    const destroy = () => {
        if (pending) window.clearTimeout(pending);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        window.removeEventListener('scroll', syncBounds);
        window.removeEventListener('resize', scheduleRefresh);
        window.removeEventListener('mousemove', onPointerMove);
        window.removeEventListener('click', askOnPileTap);
        window.removeEventListener('deviceorientation', onTilt);
        window.clearTimeout(hintTimer);
        Render.stop(render);
        cancelAnimationFrame(frameId);
        Composite.clear(world, false);
        Engine.clear(engine);
        render.canvas.remove();
        render.textures = {};
    };

    return { destroy, requestTilt };
};

const AnimationContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 4;
    pointer-events: none;
`;

const HintLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 5;
    pointer-events: none;
`;

const HintText = styled.span<{ $tappable?: boolean }>`
    position: absolute;
    display: block;
    pointer-events: ${({ $tappable }) => ($tappable ? 'auto' : 'none')};
    cursor: ${({ $tappable }) => ($tappable ? 'pointer' : 'default')};
    /* A finger needs more than a line of 12px text to hit. */
    padding: ${({ $tappable }) => ($tappable ? '10px 12px' : '0')};
    margin: ${({ $tappable }) => ($tappable ? '-10px -12px' : '0')};
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colour.LIGHT_GREY};
    white-space: nowrap;
    animation: hint-in 600ms ease both;

    @keyframes hint-in {
        from {
            opacity: 0;
            transform: translateY(4px);
        }
        to {
            opacity: 1;
            transform: none;
        }
    }
`;

const HINT_COPY: Record<Hint['mode'], string> = {
    tilt: 'tilt your phone \u2192',
    tap: 'tap to tilt \u2192',
    mouse: 'nudge the cubes \u2192',
};
/* The caption sits beside the pile, in the empty hero space, pointing at it. */
const HINT_GAP = 14;
const HINT_LINE_HEIGHT = 16;

export default function HomeAnimation() {
    const containerRef = useRef<HTMLDivElement>(null);
    const requestTilt = useRef<() => void>(() => undefined);
    const [hint, setHint] = useState<Hint | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const animation = homeAnimation(containerRef.current, setHint);
        requestTilt.current = animation.requestTilt;
        return animation.destroy;
    }, []);

    const tappable = hint?.mode === 'tap';

    return (
        <>
            <AnimationContainer ref={containerRef} aria-hidden />
            {hint && (
                <HintLayer>
                    <HintText
                        $tappable={tappable}
                        role={tappable ? 'button' : undefined}
                        onClick={tappable ? () => requestTilt.current() : undefined}
                        style={{
                            right: document.documentElement.clientWidth - hint.left + HINT_GAP,
                            top: hint.top + hint.height / 2 - HINT_LINE_HEIGHT / 2,
                        }}
                    >
                        {HINT_COPY[hint.mode]}
                    </HintText>
                </HintLayer>
            )}
        </>
    );
}
